import { BrandAccess } from '../enums/brands.js';

/**
 * Prompt template variable definitions and interpolation engine.
 *
 * Template variables use the format `{{namespace.field}}` and are replaced
 * at runtime with contextual values from the agent execution environment.
 *
 * Security: Single-pass replacement only — output of one variable expansion
 * is never re-processed for further `{{...}}` patterns.
 */

export type PromptTemplateVariable = {
  key: string;
  label: string;
  description: string;
  example: string;
  group: TemplateVariableGroupKey;
  supportedContexts?: readonly TemplateVariableContext[];
};

export type TemplateVariableContext = 'prompt' | 'sql_template';

export type TemplateVariableGroupKey =
  | 'account'
  | 'user'
  | 'brand'
  | 'subject'
  | 'resolvedEntities'
  | 'input';

export type TemplateVariableGroup = {
  key: TemplateVariableGroupKey;
  label: string;
  description?: string;
};

export const PROMPT_TEMPLATE_VARIABLE_GROUPS: TemplateVariableGroup[] = [
  { key: 'account', label: 'Account', description: 'Account-level identifiers and metadata.' },
  { key: 'user', label: 'User', description: 'Current user identity and metadata.' },
  { key: 'brand', label: 'Brand', description: 'Brand context including therapeutic hierarchy.' },
  { key: 'subject', label: 'Subject', description: 'Chat context subject (e.g. HCP).' },
  { key: 'resolvedEntities', label: 'Resolved Entities', description: 'Canonical entity resolution output.' },
  { key: 'input', label: 'Input', description: 'Runtime query and history.' },
];

const DEFAULT_SUPPORTED_CONTEXTS: readonly TemplateVariableContext[] = ['prompt', 'sql_template'];

const USER_METADATA_DYNAMIC_TEMPLATE_KEY = 'user.metadata.[prop_name]';

/**
 * All supported prompt template variables with their metadata.
 * Used by the UI to display available variables and by the interpolation engine.
 *
 * Declared with `as const satisfies` so each `key` narrows to its literal,
 * letting `PromptTemplateVariableKey` derive directly from the registry.
 */
export const PROMPT_TEMPLATE_VARIABLES = [
  {
    key: 'account.id',
    label: 'Account ID',
    description: `The unique identifier (UUID) of the user's account.`,
    example: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    group: 'account',
  },
  {
    key: 'account.name',
    label: 'Account Name',
    description: `The display name of the user's account.`,
    example: 'Acme Corp',
    group: 'account',
  },
  {
    key: 'user.id',
    label: 'User ID',
    description: 'The unique identifier (UUID) of the current user.',
    example: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
    group: 'user',
  },
  {
    key: 'user.name',
    label: 'User Name',
    description: 'The full name of the current user (first + last name).',
    example: 'Jane Smith',
    group: 'user',
  },
  {
    key: 'user.email',
    label: 'User Email',
    description: 'The email address of the current user.',
    example: 'jane.smith@example.com',
    group: 'user',
  },
  {
    key: USER_METADATA_DYNAMIC_TEMPLATE_KEY,
    label: 'User Metadata Value',
    description:
      'A dynamic user metadata value by property name. Supports both {{user.metadata.prop_name}} and {{user.metadata.[prop_name]}} syntax.',
    example: '{{user.metadata.department}}',
    group: 'user',
  },
  {
    key: 'user.brandIndications',
    label: 'User Brand Indications',
    description:
      'A newline-separated list of user product brand and indication mappings, with default indication markers.',
    example: '- Product Brand A / Indication A (default)\n- Product Brand B / Indication B',
    group: 'user',
    supportedContexts: ['prompt'],
  },
  {
    key: 'brand.id',
    label: 'Brand ID',
    description: 'The unique identifier of the current brand.',
    example: 'brand-abc123',
    group: 'brand',
  },
  {
    key: 'brand.name',
    label: 'Brand Name',
    description: 'The display name of the current brand.',
    example: 'PharmaCo Brand X',
    group: 'brand',
  },
  {
    key: 'brand.productBrand.name',
    label: 'Brand Product Brand Name',
    description: 'The name of the product brand linked to the current brand.',
    example: 'Cardio Portfolio',
    group: 'brand',
  },
  {
    key: 'brand.indication.name',
    label: 'Brand Indication Name',
    description: 'The name of the indication linked to the current brand.',
    example: 'Heart Failure',
    group: 'brand',
  },
  {
    key: 'brand.therapeuticArea.name',
    label: 'Brand Therapeutic Area Name',
    description: 'The name of the therapeutic area linked to the current brand.',
    example: 'Cardiology',
    group: 'brand',
  },
  {
    key: 'brand.productBrands.names',
    label: 'Brand Product Brands',
    description: 'A comma-separated list of product brand names linked to the current brand (list field).',
    example: 'Cardio Portfolio, Metabolic Portfolio',
    group: 'brand',
  },
  {
    key: 'brand.drugClasses.names',
    label: 'Brand Drug Classes',
    description: 'A comma-separated list of drug class names linked to the current brand.',
    example: 'GLP-1 Receptor Agonists, SGLT2 Inhibitors',
    group: 'brand',
  },
  {
    key: 'brand.genericDrugs.names',
    label: 'Brand Generic Drugs',
    description: 'A comma-separated list of generic drug names linked to the current brand.',
    example: 'Semaglutide, Empagliflozin',
    group: 'brand',
  },
  {
    key: 'brand.isNation',
    label: 'Brand Is National Access',
    description: 'Whether the current brand access scope is national.',
    example: 'true',
    group: 'brand',
  },
  {
    key: 'brand.imperatives',
    label: 'Brand Strategic Imperatives',
    description: 'The serialized strategic imperatives configured for the brand.',
    example: '<strategic_imperatives>...</strategic_imperatives>',
    group: 'brand',
    supportedContexts: ['prompt'],
  },
  {
    key: 'brand.drugClassProductBrandMappings',
    label: 'Brand Drug Class To Product Brand Mappings',
    description:
      'A newline-separated list of drug class to product brand mappings linked to the current brand.',
    example: '- Drug Class A -> Product Brand A, Product Brand B',
    group: 'brand',
    supportedContexts: ['prompt'],
  },
  {
    key: 'subject.id',
    label: 'Subject ID',
    description: 'The identifier of the chat context subject (if set).',
    example: '12345',
    group: 'subject',
  },
  {
    key: 'subject.name',
    label: 'Subject Name',
    description: 'The display name of the chat context subject (if set).',
    example: 'Dr. John Doe',
    group: 'subject',
  },
  {
    key: 'subject.kind',
    label: 'Subject Kind',
    description: 'The type/kind of the chat context subject (if set).',
    example: 'hcp',
    group: 'subject',
  },
  {
    key: 'resolvedEntities.account.id',
    label: 'Resolved Account ID',
    description:
      'Resolved account identifier. Uses resolved entities with type account first, then subject when subject kind is account.',
    example: 'account-123',
    group: 'resolvedEntities',
  },
  {
    key: 'resolvedEntities.account.name',
    label: 'Resolved Account Name',
    description:
      'Resolved account name. Uses resolved entities with type account first, then subject when subject kind is account.',
    example: 'Acme Account',
    group: 'resolvedEntities',
  },
  {
    key: 'resolvedEntities.territory.id',
    label: 'Resolved Territory ID',
    description:
      'Resolved territory identifier. Uses resolved entities with type territory first, then subject when subject kind is territory.',
    example: 'territory-456',
    group: 'resolvedEntities',
  },
  {
    key: 'resolvedEntities.territory.name',
    label: 'Resolved Territory Name',
    description:
      'Resolved territory name. Uses resolved entities with type territory first, then subject when subject kind is territory.',
    example: 'North East',
    group: 'resolvedEntities',
  },
  {
    key: 'resolvedEntities.hcp.id',
    label: 'Resolved HCP ID',
    description:
      'Resolved HCP identifier. Uses resolved entities with type hcp first, then subject when subject kind is hcp.',
    example: 'hcp-789',
    group: 'resolvedEntities',
  },
  {
    key: 'resolvedEntities.hcp.name',
    label: 'Resolved HCP Name',
    description:
      'Resolved HCP name. Uses resolved entities with type hcp first, then subject when subject kind is hcp.',
    example: 'Dr. Jane Smith',
    group: 'resolvedEntities',
  },
  {
    key: 'resolvedEntities.patient.id',
    label: 'Resolved Patient ID',
    description:
      'Resolved patient identifier. Uses resolved entities with type patient first, then subject when subject kind is patient.',
    example: 'patient-123',
    group: 'resolvedEntities',
  },
  {
    key: 'resolvedEntities.patient.name',
    label: 'Resolved Patient Name',
    description:
      'Resolved patient name. Uses resolved entities with type patient first, then subject when subject kind is patient.',
    example: 'Jane Patient',
    group: 'resolvedEntities',
  },
  {
    key: 'resolvedEntities.productBrand.name',
    label: 'Resolved Product Brand Name',
    description:
      'Resolved product brand name. Uses resolved entities with type brand first, else the user default product brand indication mapping.',
    example: 'Cardio Portfolio',
    group: 'resolvedEntities',
  },
  {
    key: 'resolvedEntities.indication.name',
    label: 'Resolved Indication Name',
    description:
      'Resolved indication name. Uses resolved entities with type indication first, else the user default product brand indication mapping.',
    example: 'Heart Failure',
    group: 'resolvedEntities',
  },
  {
    key: 'resolvedEntities.json',
    label: 'Resolved Entities JSON',
    description: 'The canonical resolved entities list as a JSON string.',
    example: '[{"id":"12345","name":"Dr. John Doe","type":"hcp","source":"subject"}]',
    group: 'resolvedEntities',
  },
  {
    key: 'input.userQuery',
    label: 'Input User Query',
    description: 'The current user query text being evaluated by the prompt.',
    example: 'What are the latest approved indications for Drug X?',
    group: 'input',
    supportedContexts: ['prompt'],
  },
  {
    key: 'input.currentDate',
    label: 'Input Current Date',
    description: 'The current UTC date in YYYY-MM-DD format.',
    example: '2026-06-25',
    group: 'input',
  },
  {
    key: 'input.chatHistory',
    label: 'Input Chat History',
    description: 'Formatted prior chat history provided to the prompt at runtime.',
    example: 'USER:\nWhat is Drug X?\n\nASSISTANT:\nDrug X is ...',
    group: 'input',
    supportedContexts: ['prompt'],
  },
] as const satisfies readonly PromptTemplateVariable[];

/**
 * Literal union of all keys in `PROMPT_TEMPLATE_VARIABLES`, plus any
 * runtime-resolved `user.metadata.<prop_name>` slot. This is the canonical
 * parameter-slot vocabulary shared between prompt interpolation and SQL
 * template `${var}` placeholders.
 */
export type PromptTemplateVariableKey =
  | Exclude<(typeof PROMPT_TEMPLATE_VARIABLES)[number]['key'], 'user.metadata.[prop_name]'>
  | `user.metadata.${string}`;

export function isPromptTemplateVariableSupportedInContext(data: {
  variable: PromptTemplateVariable;
  context: TemplateVariableContext;
}): boolean {
  const supportedContexts = data.variable.supportedContexts ?? DEFAULT_SUPPORTED_CONTEXTS;
  return supportedContexts.includes(data.context);
}

export function getPromptTemplateVariablesForContext(
  context: TemplateVariableContext
): PromptTemplateVariable[] {
  return PROMPT_TEMPLATE_VARIABLES.filter((variable) =>
    isPromptTemplateVariableSupportedInContext({ variable, context })
  );
}

export type PromptTemplateVariableGrouped = {
  group: TemplateVariableGroup;
  variables: PromptTemplateVariable[];
};

export function getPromptTemplateVariablesGroupedForContext(
  context: TemplateVariableContext
): PromptTemplateVariableGrouped[] {
  const contextVariables = getPromptTemplateVariablesForContext(context);

  return PROMPT_TEMPLATE_VARIABLE_GROUPS.map((group) => ({
    group,
    variables: contextVariables.filter((variable) => variable.group === group.key),
  })).filter((entry) => entry.variables.length > 0);
}

/** All known variable keys for fast lookup */
const KNOWN_KEYS: ReadonlySet<string> = new Set<string>(
  PROMPT_TEMPLATE_VARIABLES.map((variable) => variable.key).filter(
    (variableKey) => variableKey !== USER_METADATA_DYNAMIC_TEMPLATE_KEY
  )
);

/**
 * Context values available for template interpolation at runtime.
 * All values are nullable — if a value is null/undefined, the placeholder is left as-is.
 */
export type PromptTemplateContext = {
  'account.id': string | null;
  'account.name': string | null;
  'user.id': string | null;
  'user.name': string | null;
  'user.email': string | null;
  'user.metadata': Record<string, unknown>;
  'user.brandIndications': string | null;
  'brand.id': string | null;
  'brand.name': string | null;
  'brand.productBrand.name': string | null;
  'brand.indication.name': string | null;
  'brand.therapeuticArea.name': string | null;
  'brand.productBrands.names': string | null;
  'brand.drugClasses.names': string | null;
  'brand.genericDrugs.names': string | null;
  'brand.isNation': string | null;
  'brand.imperatives': string | null;
  'brand.drugClassProductBrandMappings': string | null;
  'subject.id': string | null;
  'subject.name': string | null;
  'subject.kind': string | null;
  'resolvedEntities.account.id': string | null;
  'resolvedEntities.account.name': string | null;
  'resolvedEntities.territory.id': string | null;
  'resolvedEntities.territory.name': string | null;
  'resolvedEntities.hcp.id': string | null;
  'resolvedEntities.hcp.name': string | null;
  'resolvedEntities.patient.id': string | null;
  'resolvedEntities.patient.name': string | null;
  'resolvedEntities.productBrand.name': string | null;
  'resolvedEntities.indication.name': string | null;
  'resolvedEntities.json': string | null;
  'input.userQuery': string | null;
  'input.currentDate': string | null;
  'input.chatHistory': string | null;
};

export const EMPTY_PROMPT_TEMPLATE_CONTEXT: PromptTemplateContext = {
  'account.id': null,
  'account.name': null,
  'user.id': null,
  'user.name': null,
  'user.email': null,
  'user.metadata': {},
  'user.brandIndications': null,
  'brand.id': null,
  'brand.name': null,
  'brand.productBrand.name': null,
  'brand.indication.name': null,
  'brand.therapeuticArea.name': null,
  'brand.productBrands.names': null,
  'brand.drugClasses.names': null,
  'brand.genericDrugs.names': null,
  'brand.isNation': null,
  'brand.imperatives': null,
  'brand.drugClassProductBrandMappings': null,
  'subject.id': null,
  'subject.name': null,
  'subject.kind': null,
  'resolvedEntities.account.id': null,
  'resolvedEntities.account.name': null,
  'resolvedEntities.territory.id': null,
  'resolvedEntities.territory.name': null,
  'resolvedEntities.hcp.id': null,
  'resolvedEntities.hcp.name': null,
  'resolvedEntities.patient.id': null,
  'resolvedEntities.patient.name': null,
  'resolvedEntities.productBrand.name': null,
  'resolvedEntities.indication.name': null,
  'resolvedEntities.json': null,
  'input.userQuery': null,
  'input.currentDate': null,
  'input.chatHistory': null,
};

type PromptResolvedEntity = {
  id: string;
  name: string;
  type: string;
  source: string;
};

export type BuildPromptTemplateContextParams = {
  user: {
    identifier: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
    metadata?: Record<string, unknown>;
    brandIndications?: Array<{
      productBrandName: string | null;
      indicationName: string | null;
      isDefault: boolean;
    }>;
    group: {
      identifier: string;
      name: string;
    };
  };
  brand: {
    identifier: string;
    name: string;
    access?: BrandAccess;
    productBrand?: {
      name: string | null;
    } | null;
    indication?: {
      name: string | null;
    } | null;
    therapeuticArea?: {
      name: string | null;
    } | null;
    productBrands?: Array<{
      name: string | null;
      displayName?: string | null;
    }> | null;
    drugClasses?: Array<{
      name: string | null;
      displayName?: string | null;
    }> | null;
    genericDrugs?: Array<{
      name: string | null;
      displayName?: string | null;
    }> | null;
    drugClassProductBrandMappings?: Array<{
      drugClass: {
        name: string | null;
        displayName?: string | null;
      };
      productBrands: Array<{
        name: string | null;
        displayName?: string | null;
      }>;
    }> | null;
  };
  subject: {
    id: number | string;
    name: string;
    kind?: string;
  } | null;
  resolvedEntities?: PromptResolvedEntity[];
  brandImperatives: string | null;
  input?: {
    userQuery?: string | null;
    chatHistory?: string | null;
  };
};

function mapBrandAccessToNationFlag(access: BrandAccess | undefined): string | null {
  if (access == null) {
    return null;
  }

  return access === BrandAccess.National ? 'true' : 'false';
}

function buildBrandTemplateContextValues(
  brand: BuildPromptTemplateContextParams['brand']
): Pick<
  PromptTemplateContext,
  | 'brand.id'
  | 'brand.name'
  | 'brand.productBrand.name'
  | 'brand.indication.name'
  | 'brand.therapeuticArea.name'
  | 'brand.productBrands.names'
  | 'brand.drugClasses.names'
  | 'brand.genericDrugs.names'
  | 'brand.isNation'
  | 'brand.drugClassProductBrandMappings'
> {
  const serializeLookupList = (
    values:
      | Array<{
          name: string | null;
          displayName?: string | null;
        }>
      | null
      | undefined
  ): string | null => {
    if (!values || values.length === 0) {
      return null;
    }

    const renderedValues = values
      .map((value) => {
        return (value.displayName ?? value.name ?? '').trim();
      })
      .filter((value) => value.length > 0);

    if (renderedValues.length === 0) {
      return null;
    }

    return renderedValues.join(', ');
  };

  const serializeDrugClassProductBrandMappings = (
    mappings: BuildPromptTemplateContextParams['brand']['drugClassProductBrandMappings']
  ): string | null => {
    if (!mappings || mappings.length === 0) {
      return null;
    }

    const renderedValues = mappings
      .map((mapping) => {
        const drugClassName = (mapping.drugClass.displayName ?? mapping.drugClass.name ?? '').trim();
        if (drugClassName.length === 0) {
          return null;
        }

        const productBrandNames = mapping.productBrands
          .map((productBrand) => (productBrand.displayName ?? productBrand.name ?? '').trim())
          .filter((name) => name.length > 0);

        if (productBrandNames.length === 0) {
          return null;
        }

        return `- ${drugClassName} -> ${productBrandNames.join(', ')}`;
      })
      .filter((line): line is string => line !== null);

    if (renderedValues.length === 0) {
      return null;
    }

    return renderedValues.join('\n');
  };

  return {
    'brand.id': brand.identifier,
    'brand.name': brand.name,
    'brand.productBrand.name': brand.productBrand?.name ?? null,
    'brand.indication.name': brand.indication?.name ?? null,
    'brand.therapeuticArea.name': brand.therapeuticArea?.name ?? null,
    'brand.productBrands.names': serializeLookupList(brand.productBrands),
    'brand.drugClasses.names': serializeLookupList(brand.drugClasses),
    'brand.genericDrugs.names': serializeLookupList(brand.genericDrugs),
    'brand.isNation': mapBrandAccessToNationFlag(brand.access),
    'brand.drugClassProductBrandMappings': serializeDrugClassProductBrandMappings(
      brand.drugClassProductBrandMappings
    ),
  };
}

function buildUserTemplateContextValues(
  user: BuildPromptTemplateContextParams['user']
): Pick<
  PromptTemplateContext,
  | 'account.id'
  | 'account.name'
  | 'user.id'
  | 'user.name'
  | 'user.email'
  | 'user.metadata'
  | 'user.brandIndications'
> {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || null;

  const serializedBrandIndications =
    user.brandIndications
      ?.map((item) => {
        const productBrandName = (item.productBrandName ?? '').trim();
        const indicationName = (item.indicationName ?? '').trim();
        if (productBrandName.length === 0 || indicationName.length === 0) {
          return null;
        }

        const defaultSuffix = item.isDefault ? ' (default)' : '';
        return `- ${productBrandName} / ${indicationName}${defaultSuffix}`;
      })
      .filter((line): line is string => line !== null)
      .join('\n') ?? null;

  return {
    'account.id': user.group.identifier,
    'account.name': user.group.name,
    'user.id': user.identifier,
    'user.name': fullName,
    'user.email': user.email,
    'user.metadata': user.metadata ?? {},
    'user.brandIndications':
      serializedBrandIndications && serializedBrandIndications.length > 0 ? serializedBrandIndications : null,
  };
}

function buildSubjectTemplateContextValues(
  subject: BuildPromptTemplateContextParams['subject']
): Pick<PromptTemplateContext, 'subject.id' | 'subject.name' | 'subject.kind'> {
  return {
    'subject.id': subject != null ? String(subject.id) : null,
    'subject.name': subject?.name ?? null,
    'subject.kind': subject?.kind ?? null,
  };
}

function buildResolvedEntitiesTemplateContextValues(data: {
  resolvedEntities: BuildPromptTemplateContextParams['resolvedEntities'];
  subject: BuildPromptTemplateContextParams['subject'];
  user: BuildPromptTemplateContextParams['user'];
}): Pick<
  PromptTemplateContext,
  | 'resolvedEntities.account.id'
  | 'resolvedEntities.account.name'
  | 'resolvedEntities.territory.id'
  | 'resolvedEntities.territory.name'
  | 'resolvedEntities.hcp.id'
  | 'resolvedEntities.hcp.name'
  | 'resolvedEntities.patient.id'
  | 'resolvedEntities.patient.name'
  | 'resolvedEntities.productBrand.name'
  | 'resolvedEntities.indication.name'
  | 'resolvedEntities.json'
> {
  const normalizeType = (value: string | null | undefined): string => value?.trim().toLowerCase() ?? '';

  const findEntityByType = (type: string): PromptResolvedEntity | null => {
    const targetType = normalizeType(type);
    if (!data.resolvedEntities || data.resolvedEntities.length === 0) {
      return null;
    }

    for (const entity of data.resolvedEntities) {
      if (normalizeType(entity.type) === targetType) {
        return entity;
      }
    }

    return null;
  };

  const resolveFromEntityThenSubject = (
    type: 'account' | 'territory' | 'hcp' | 'patient'
  ): { id: string | null; name: string | null } => {
    const entity = findEntityByType(type);
    if (entity !== null) {
      return {
        id: entity.id,
        name: entity.name,
      };
    }

    if (normalizeType(data.subject?.kind) === type && data.subject != null) {
      return {
        id: String(data.subject.id),
        name: data.subject.name,
      };
    }

    return {
      id: null,
      name: null,
    };
  };

  const resolveDefaultBrandIndication = (): {
    productBrandName: string | null;
    indicationName: string | null;
  } => {
    const defaultMapping = data.user.brandIndications?.find((item) => item.isDefault);
    if (!defaultMapping) {
      return {
        productBrandName: null,
        indicationName: null,
      };
    }

    const productBrandName = (defaultMapping.productBrandName ?? '').trim();
    const indicationName = (defaultMapping.indicationName ?? '').trim();

    return {
      productBrandName: productBrandName.length > 0 ? productBrandName : null,
      indicationName: indicationName.length > 0 ? indicationName : null,
    };
  };

  const resolvedAccount = resolveFromEntityThenSubject('account');
  const resolvedTerritory = resolveFromEntityThenSubject('territory');
  const resolvedHcp = resolveFromEntityThenSubject('hcp');
  const resolvedPatient = resolveFromEntityThenSubject('patient');

  const resolvedProductBrandFromEntity = findEntityByType('brand')?.name ?? null;
  const resolvedIndicationFromEntity = findEntityByType('indication')?.name ?? null;
  const defaultBrandIndication = resolveDefaultBrandIndication();

  const resolvedProductBrandName =
    resolvedProductBrandFromEntity && resolvedProductBrandFromEntity.trim().length > 0
      ? resolvedProductBrandFromEntity
      : defaultBrandIndication.productBrandName;

  const resolvedIndicationName =
    resolvedIndicationFromEntity && resolvedIndicationFromEntity.trim().length > 0
      ? resolvedIndicationFromEntity
      : defaultBrandIndication.indicationName;

  return {
    'resolvedEntities.account.id': resolvedAccount.id,
    'resolvedEntities.account.name': resolvedAccount.name,
    'resolvedEntities.territory.id': resolvedTerritory.id,
    'resolvedEntities.territory.name': resolvedTerritory.name,
    'resolvedEntities.hcp.id': resolvedHcp.id,
    'resolvedEntities.hcp.name': resolvedHcp.name,
    'resolvedEntities.patient.id': resolvedPatient.id,
    'resolvedEntities.patient.name': resolvedPatient.name,
    'resolvedEntities.productBrand.name': resolvedProductBrandName,
    'resolvedEntities.indication.name': resolvedIndicationName,
    'resolvedEntities.json':
      data.resolvedEntities !== undefined ? JSON.stringify(data.resolvedEntities) : null,
  };
}

/**
 * Build the template context from runtime agent execution data.
 */
export function buildPromptTemplateContext(params: BuildPromptTemplateContextParams): PromptTemplateContext {
  const userTemplateValues = buildUserTemplateContextValues(params.user);
  const brandTemplateValues = buildBrandTemplateContextValues(params.brand);
  const subjectTemplateValues = buildSubjectTemplateContextValues(params.subject);
  const currentDate = new Date().toISOString().slice(0, 10);
  const resolvedEntitiesTemplateValues = buildResolvedEntitiesTemplateContextValues({
    resolvedEntities: params.resolvedEntities,
    subject: params.subject,
    user: params.user,
  });

  return {
    ...userTemplateValues,
    ...brandTemplateValues,
    'brand.imperatives': params.brandImperatives,
    ...subjectTemplateValues,
    ...resolvedEntitiesTemplateValues,
    'input.userQuery': params.input?.userQuery ?? null,
    'input.currentDate': currentDate,
    'input.chatHistory': params.input?.chatHistory ?? null,
  };
}

/**
 * Pattern matches `{{namespace.field}}` with optional whitespace inside braces.
 * Matches any non-brace key content; replacement safety is enforced later
 * by known-key checks and metadata key parsing.
 */
const TEMPLATE_PATTERN = /\{\{\s*([^{}]+?)\s*\}\}/g;

function parseUserMetadataKey(key: string): string | null {
  const prefix = 'user.metadata.';
  if (!key.startsWith(prefix)) {
    return null;
  }

  const raw = key.slice(prefix.length).trim();
  if (raw.length === 0) {
    return null;
  }

  if (raw.startsWith('[') && raw.endsWith(']')) {
    const inner = raw.slice(1, -1).trim();
    return inner.length > 0 ? inner : null;
  }

  return raw;
}

export function resolvePromptTemplateVariableValue(
  context: PromptTemplateContext,
  key: string
): string | null {
  const metadataKey = parseUserMetadataKey(key);
  if (metadataKey !== null) {
    const metadataValue = context['user.metadata'][metadataKey];
    return stringifyTemplateValue(metadataValue);
  }

  if (!KNOWN_KEYS.has(key)) {
    return null;
  }

  const value = context[key as keyof PromptTemplateContext];
  return typeof value === 'string' ? value : null;
}

function stringifyTemplateValue(value: unknown): string | null {
  if (value == null) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }

  return JSON.stringify(value);
}

/**
 * Interpolate template variables in a prompt string.
 *
 * - Single-pass replacement only (no recursive expansion).
 * - Only known variable keys are replaced; unknown `{{foo.bar}}` are left as-is.
 * - Null/undefined context values leave the placeholder untouched.
 */
export function interpolatePromptTemplate(template: string, context: PromptTemplateContext): string {
  return template.replace(TEMPLATE_PATTERN, (match, key: string) => {
    const renderedValue = resolvePromptTemplateVariableValue(context, key);
    if (renderedValue == null) {
      return match;
    }
    return renderedValue;
  });
}
